import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { ActivityService } from '../../../services/activity-services';

type FormatQr = 'affiche' | 'qr';

@Component({
  selector: 'app-qrcode-affiche',
  templateUrl: './qrcode-affiche.component.html',
  styleUrls: ['./qrcode-affiche.component.css'],
})
export class QrcodeAfficheComponent implements OnDestroy {
  @Input() activite: any;
  @Input() libelleType = 'Atelier Force-N';
  @Output() fermer = new EventEmitter<void>();

  // Textes de l'affiche (parcours : scan → formulaire → email de l'invitation)
  readonly TEXTE_PASTILLE = 'Pointage';
  readonly TEXTE_CONSIGNE = 'Scannez pour valider votre présence';
  readonly TEXTE_ETAPES =
    '1. Scannez le QR code     2. Saisissez votre email     3. Validez';
  readonly TEXTE_RAPPEL =
    'Utilisez l’adresse email sur laquelle vous avez reçu l’invitation';

  format: FormatQr = 'affiche';
  etape: 'confirmation' | 'affiche' = 'confirmation'; // on commence par la confirmation
  chargement = false;
  erreur = false;
  apercuUrl: string | null = null;

  private qrObjectUrl: string | null = null;
  private qrImage: HTMLImageElement | null = null;

  private readonly POLICE = 'Inter, Arial, sans-serif';
  private readonly BLEU = '#1366a6';
  private readonly BLEU_FONCE = '#0f4f82';
  private readonly TEXTE = '#101828';
  private readonly GRIS = '#667085';

  // ===== DÉBUT AJOUT : logos déjà présents dans le projet (adapte les chemins) =====
  private readonly LOGO_UNCHK = 'assets/img/logo_unchk.png';
  private readonly LOGO_FORCEN = 'assets/img/FN-LOGO.png';
  private logoUnchk: HTMLImageElement | null = null;
  private logoForcen: HTMLImageElement | null = null;
  // ===== FIN AJOUT =====

  constructor(private activityService: ActivityService) {}

  // ===== MODIF : on charge les logos en parallèle du QR code =====
  generer(): void {
    this.etape = 'affiche';
    this.chargement = true;
    this.erreur = false;

    const logos = Promise.all([
      this.chargerImage(this.LOGO_UNCHK),
      this.chargerImage(this.LOGO_FORCEN),
    ]).then(([unchk, forcen]) => {
      this.logoUnchk = unchk;
      this.logoForcen = forcen;
    });

    this.activityService.generateActivityQRCode(this.activite.id).subscribe({
      next: (blob: Blob) => {
        this.qrObjectUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          this.qrImage = img;
          logos.then(() => this.mettreAJourApercu());
        };
        img.onerror = () => this.signalerErreur();
        img.src = this.qrObjectUrl;
      },
      error: () => this.signalerErreur(),
    });
  }
  // ===== FIN MODIF =====

  ngOnDestroy(): void {
    if (this.qrObjectUrl) {
      URL.revokeObjectURL(this.qrObjectUrl);
    }
  }

  @HostListener('document:keydown.escape')
  onEchap(): void {
    this.fermer.emit();
  }

  choisirFormat(format: FormatQr): void {
    if (this.format !== format) {
      this.format = format;
      this.mettreAJourApercu();
    }
  }

  telecharger(): void {
    const canvas = this.dessiner();
    if (!canvas) {
      return;
    }
    canvas.toBlob((blob) => {
      if (!blob) {
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // MODIF : nom de l'activité dans le nom du fichier
      a.download =
        this.format === 'affiche'
          ? `affiche_qrcode_${this.nomFichier()}.png`
          : `qrcode_${this.nomFichier()}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  }

  /** Ouvre une fenêtre qui ne contient que l'affiche, puis lance l'impression */
  imprimer(): void {
    const canvas = this.dessiner();
    if (!canvas) {
      return;
    }
    const fenetre = window.open('', '_blank');
    if (!fenetre) {
      return; // fenêtre bloquée par le navigateur
    }
    const doc = fenetre.document;
    doc.open();
    doc.write(
      '<!doctype html><html><head><meta charset="utf-8">' +
        '<style>@page{size:A4;margin:0}html,body{margin:0}img{display:block;height:auto}</style>' +
        '</head><body></body></html>',
    );
    doc.close();
    doc.title = `QR code – ${this.activite?.nomActivity ?? ''}`;

    const img = doc.createElement('img');
    img.style.width = this.format === 'affiche' ? '100%' : '60%';
    img.style.margin = this.format === 'affiche' ? '0' : '20mm auto';
    img.onload = () => {
      fenetre.focus();
      fenetre.print();
    };
    img.src = canvas.toDataURL('image/png');
    doc.body.appendChild(img);
  }

  // ---------- Dessin ----------

  private mettreAJourApercu(): void {
    const lancer = () => {
      const canvas = this.dessiner();
      this.apercuUrl = canvas ? canvas.toDataURL('image/png') : null;
      this.chargement = false;
    };
    // On attend que la police Inter soit chargée pour que l'affiche soit nette
    const polices = (document as any).fonts;
    polices?.ready ? polices.ready.then(lancer) : lancer();
  }

  private signalerErreur(): void {
    this.erreur = true;
    this.chargement = false;
  }

  private dessiner(): HTMLCanvasElement | null {
    if (!this.qrImage) {
      return null;
    }
    return this.format === 'affiche'
      ? this.dessinerAffiche(this.qrImage)
      : this.dessinerQrSeul(this.qrImage);
  }

  private dessinerQrSeul(qr: HTMLImageElement): HTMLCanvasElement {
    const taille = 1000;
    const marge = 60;
    const { canvas, ctx } = this.creerCanvas(taille, taille);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, taille, taille);
    ctx.imageSmoothingEnabled = false; // QR net, sans flou
    ctx.drawImage(qr, marge, marge, taille - 2 * marge, taille - 2 * marge);
    return canvas;
  }

  /** Affiche A4 (1240 × 1754 px ≈ 150 dpi) */
  /** Affiche A4 (1240 × 1754 px ≈ 150 dpi) */
  private dessinerAffiche(qr: HTMLImageElement): HTMLCanvasElement {
    const W = 1240;
    const H = 1754;
    const M = 90;
    const L = W - 2 * M;
    const { canvas, ctx } = this.creerCanvas(W, H);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    ctx.textBaseline = 'alphabetic';

    // Titre en titlecase, 2 lignes maximum
    ctx.font = `700 60px ${this.POLICE}`;
    const toutes = this.decouper(
      ctx,
      this.enTitre(this.activite?.nomActivity ?? ''),
      L,
    );
    const lignesTitre = toutes.slice(0, 2);
    if (toutes.length > 2) {
      lignesTitre[1] = this.tronquer(
        ctx,
        `${lignesTitre[1]} ${toutes.slice(2).join(' ')}`,
        L,
      );
    }

    // Bande blanche avec les logos (UN-CHK à gauche, Force-N à droite)
    const hauteurLogos = 150;
    if (this.logoUnchk) {
      this.dessinerLogo(ctx, this.logoUnchk, M, hauteurLogos / 2, 90, 380);
    } else {
      ctx.fillStyle = this.TEXTE;
      ctx.textAlign = 'left';
      ctx.font = `700 30px ${this.POLICE}`;
      ctx.fillText('UN-CHK', M, hauteurLogos / 2 + 10);
    }
    if (this.logoForcen) {
      this.dessinerLogo(
        ctx,
        this.logoForcen,
        W - M,
        hauteurLogos / 2,
        90,
        380,
        true,
      );
    } else {
      ctx.fillStyle = this.BLEU;
      ctx.textAlign = 'right';
      ctx.font = `800 40px ${this.POLICE}`;
      ctx.fillText('FORCE-N', W - M, hauteurLogos / 2 + 14);
    }

    // Bandeau bleu : type + titre
    const hauteurBandeau = hauteurLogos + 210 + (lignesTitre.length - 1) * 72;
    const degrade = ctx.createLinearGradient(
      0,
      hauteurLogos,
      W,
      hauteurBandeau,
    );
    degrade.addColorStop(0, this.BLEU_FONCE);
    degrade.addColorStop(0.6, this.BLEU);
    degrade.addColorStop(1, '#2b86c9');
    ctx.fillStyle = degrade;
    ctx.fillRect(0, hauteurLogos, W, hauteurBandeau - hauteurLogos);

    ctx.save(); // cercle décoratif limité au bandeau
    ctx.beginPath();
    ctx.rect(0, hauteurLogos, W, hauteurBandeau - hauteurLogos);
    ctx.clip();
    ctx.fillStyle = 'rgba(255,255,255,0.08)';
    ctx.beginPath();
    ctx.arc(W - 40, hauteurLogos + 20, 260, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'left';
    ctx.globalAlpha = 0.85;
    ctx.font = `600 26px ${this.POLICE}`;
    ctx.fillText(this.libelleType.toUpperCase(), M, hauteurLogos + 75);
    ctx.globalAlpha = 1;
    ctx.font = `700 60px ${this.POLICE}`;
    lignesTitre.forEach((ligne, i) =>
      ctx.fillText(ligne, M, hauteurLogos + 150 + i * 72),
    );

    // Pastille
    let y = hauteurBandeau + 70;
    ctx.font = `600 26px ${this.POLICE}`;
    ctx.textAlign = 'center';
    const largeurPastille = ctx.measureText(this.TEXTE_PASTILLE).width + 48;
    ctx.fillStyle = '#eef2ff';
    this.rectArrondi(
      ctx,
      (W - largeurPastille) / 2,
      y - 36,
      largeurPastille,
      52,
      26,
    );
    ctx.fill();
    ctx.fillStyle = '#4338ca';
    ctx.fillText(this.TEXTE_PASTILLE, W / 2, y - 2);

    // Date (début), heure, lieu
    y += 50;
    const infos = [
      { libelle: 'DATE', valeur: this.dateTexte() },
      { libelle: 'HEURE', valeur: this.heureTexte() },
      { libelle: 'LIEU', valeur: this.activite?.adresse || '—' },
    ];
    const ecart = 24;
    const largeurCase = (L - 2 * ecart) / 3;
    const hauteurCase = 120;
    infos.forEach((info, i) => {
      const x = M + i * (largeurCase + ecart);
      ctx.fillStyle = '#f9fafb';
      ctx.strokeStyle = '#eaecf0';
      ctx.lineWidth = 2;
      this.rectArrondi(ctx, x, y, largeurCase, hauteurCase, 18);
      ctx.fill();
      ctx.stroke();

      ctx.textAlign = 'left';
      ctx.fillStyle = this.GRIS;
      ctx.font = `600 20px ${this.POLICE}`;
      ctx.fillText(info.libelle, x + 24, y + 44);

      ctx.fillStyle = this.TEXTE;
      ctx.font = `700 30px ${this.POLICE}`;
      ctx.fillText(
        this.tronquer(ctx, info.valeur, largeurCase - 48),
        x + 24,
        y + 90,
      );
    });

    // QR code encadré
    y += hauteurCase + 70;
    const tailleQr = 560;
    const marge = 30;
    const cadre = tailleQr + 2 * marge;
    const xCadre = (W - cadre) / 2;
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = this.BLEU;
    ctx.lineWidth = 5;
    this.rectArrondi(ctx, xCadre, y, cadre, cadre, 32);
    ctx.fill();
    ctx.stroke();
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(qr, xCadre + marge, y + marge, tailleQr, tailleQr);
    ctx.imageSmoothingEnabled = true;

    // Étiquette "SCANNEZ-MOI"
    ctx.font = `700 24px ${this.POLICE}`;
    const etiquette = 'SCANNEZ-MOI';
    const largeurEtiquette = ctx.measureText(etiquette).width + 44;
    ctx.fillStyle = this.BLEU;
    this.rectArrondi(
      ctx,
      (W - largeurEtiquette) / 2,
      y - 22,
      largeurEtiquette,
      44,
      22,
    );
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.fillText(etiquette, W / 2, y + 9);

    // Consigne + étapes
    y += cadre + 80;
    ctx.fillStyle = this.TEXTE;
    ctx.font = `700 46px ${this.POLICE}`;
    ctx.fillText(this.tronquer(ctx, this.TEXTE_CONSIGNE, L), W / 2, y);
    ctx.fillStyle = '#475467';
    ctx.font = `500 26px ${this.POLICE}`;
    ctx.fillText(this.TEXTE_ETAPES, W / 2, y + 62);

    // Rappel : l'email de l'invitation
    ctx.font = `600 26px ${this.POLICE}`;
    const rappel = this.tronquer(ctx, this.TEXTE_RAPPEL, L - 60);
    const largeurRappel = ctx.measureText(rappel).width + 60;
    ctx.fillStyle = '#fff4e5';
    ctx.strokeStyle = '#fec84b';
    ctx.lineWidth = 2;
    this.rectArrondi(
      ctx,
      (W - largeurRappel) / 2,
      y + 100,
      largeurRappel,
      64,
      14,
    );
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#b54708';
    ctx.textAlign = 'center';
    ctx.fillText(rappel, W / 2, y + 141);

    // Pied de page : seulement le site, centré
    ctx.strokeStyle = '#eaecf0';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(M, H - 110);
    ctx.lineTo(W - M, H - 110);
    ctx.stroke();
    ctx.fillStyle = this.GRIS;
    ctx.font = `500 22px ${this.POLICE}`;
    ctx.textAlign = 'center';
    ctx.fillText('force-n.sn', W / 2, H - 60);

    return canvas;
  }

  // ---------- Outils ----------

  private creerCanvas(largeur: number, hauteur: number) {
    const canvas = document.createElement('canvas');
    canvas.width = largeur;
    canvas.height = hauteur;
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    return { canvas, ctx };
  }

  private rectArrondi(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    l: number,
    h: number,
    r: number,
  ): void {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + l, y, x + l, y + h, r);
    ctx.arcTo(x + l, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + l, y, r);
    ctx.closePath();
  }

  /** Coupe un texte en lignes qui tiennent dans la largeur donnée */
  private decouper(
    ctx: CanvasRenderingContext2D,
    texte: string,
    largeurMax: number,
  ): string[] {
    const mots = texte.split(/\s+/).filter(Boolean);
    const lignes: string[] = [];
    let ligne = '';
    for (const mot of mots) {
      const essai = ligne ? `${ligne} ${mot}` : mot;
      if (ctx.measureText(essai).width > largeurMax && ligne) {
        lignes.push(ligne);
        ligne = mot;
      } else {
        ligne = essai;
      }
    }
    if (ligne) {
      lignes.push(ligne);
    }
    return lignes.length ? lignes : [''];
  }

  /** Raccourcit un texte avec "…" s'il dépasse la largeur */
  private tronquer(
    ctx: CanvasRenderingContext2D,
    texte: string,
    largeurMax: number,
  ): string {
    if (ctx.measureText(texte).width <= largeurMax) {
      return texte;
    }
    let t = texte;
    while (t.length > 1 && ctx.measureText(`${t}…`).width > largeurMax) {
      t = t.slice(0, -1);
    }
    return `${t.trimEnd()}…`;
  }

  // ===== DÉBUT AJOUT : logos, titlecase, nom de fichier =====
  private chargerImage(src: string): Promise<HTMLImageElement | null> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null); // logo introuvable → texte à la place
      img.src = src;
    });
  }

  /** Dessine un logo sans le déformer, centré verticalement sur yCentre */
  private dessinerLogo(
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    x: number,
    yCentre: number,
    hauteurMax: number,
    largeurMax: number,
    alignerADroite = false,
  ): void {
    if (!img.naturalWidth || !img.naturalHeight) {
      return;
    }
    const ratio = Math.min(
      hauteurMax / img.naturalHeight,
      largeurMax / img.naturalWidth,
    );
    const l = img.naturalWidth * ratio;
    const h = img.naturalHeight * ratio;
    ctx.drawImage(img, alignerADroite ? x - l : x, yCentre - h / 2, l, h);
  }

  /** "ATELIER forceN qr code" → "Atelier Forcen Qr Code" (comme le pipe titlecase) */
  enTitre(texte: string): string {
    return texte
      .toLowerCase()
      .split(' ')
      .map((mot) => (mot ? mot.charAt(0).toUpperCase() + mot.slice(1) : mot))
      .join(' ');
  }

  /** "Atelier ForceN QR CODE" → "atelier-forcen-qr-code" (sans accents ni caractères spéciaux) */
  private nomFichier(): string {
    const nom = (this.activite?.nomActivity ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60);
    return nom || String(this.activite?.id ?? 'activite');
  }
  // ===== FIN AJOUT =====

  private versDate(valeur: unknown): Date | null {
    if (!valeur) {
      return null;
    }
    if (typeof valeur === 'string') {
      const [a, m, j] = valeur.slice(0, 10).split('-').map(Number);
      return new Date(a, m - 1, j);
    }
    return new Date(valeur as any);
  }

  // ===== MODIF : date de début seulement =====
  private dateTexte(): string {
    const debut = this.versDate(this.activite?.openDate);
    return debut
      ? debut.toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '—';
  }

  private heureTexte(): string {
    const h = this.activite?.hours;
    return h ? String(h).slice(0, 5) : '—';
  }
}
